import { batch } from 'react-redux';
import { captureException } from '@sentry/node';
import dayjs from 'dayjs';

import { doRequest } from '@r1-frontend/do-request';
import { vas } from '@r1-frontend/api-domru/api-profile/v1/vas';
import { IVasInfo } from '@r1-frontend/api-domru/api-profile/v1/vas/dto/ResponseDto';
import { IGetLinkResponseDto, profileLink } from '@r1-frontend/api-domru/gamers-base/v1/profile/link';

import { selectSubscriptions } from '~/src/features/domru-tv/packages/store/selectors';
import { EVasCategory, EVasContentType } from '~/src/features/lk/vases/types/EVasCategory';
import { TAppThunkDispatch, TDispatch, TGetState } from '~/src/store';
import {
    loadingVas,
    setGamersBaseUrl,
    vasAntivirusLoaded,
    vasAntivirusLoading,
    vasDisablingStatusChange,
    vasLoaded,
    vasLoadError,
    vasSubscriptionsError,
    vasSubscriptionsLoaded,
    vasSubscriptionsLoading,
} from '~/src/store/actionCreators/vas';
import { EVasDisablingStatus } from '~/src/store/reducers/vas';
import { selectDomain } from '~/src/store/selectors/domain';
import selectProviderId from '~/src/store/selectors/selectProviderId';
import { selectTerminalResource } from '~/src/store/selectors/service';

const LOAD_TRESHOLD = 3000;
const GAMERS_BASE_SERVICE_IDS = [102718, 104873, 104879]; // ID сервисов Gamers Base

type TLoadVasOptions = {
    isSkipLoadCheck?: boolean,
    withAntiviruses?: boolean,
}

export const prepareCategories = (categories: EVasCategory[]) => `${categories.join(';')};`;

export function loadVas(
    {
        isSkipLoadCheck = false,
        withAntiviruses = true,
    }: TLoadVasOptions = {},
) {
    return async(dispatch: TAppThunkDispatch, getState: TGetState): Promise<void> => {
        const state = getState();
        const loadTime = state.vas.loaded;

        if (
            !isSkipLoadCheck &&
            loadTime &&
            dayjs().unix() - loadTime < LOAD_TRESHOLD
        ) {
            return;
        }

        batch(() => {
            dispatch(vasAntivirusLoading());
            dispatch(loadingVas());
        });

        const domain = selectDomain(state);
        const trId = selectTerminalResource(state);
        const providerId = selectProviderId(state);

        const [vasResponse, gamesBaseLinkResponse] = await Promise.all([
            doRequest.apiProfile<IVasInfo[]>(
                vas({
                    categories: prepareCategories([
                        EVasCategory.antiviruses,
                        EVasCategory.onlineCinemas,
                        EVasCategory.watchControl,
                        EVasCategory.watchEverywhere,
                        EVasCategory.games,
                        EVasCategory.multimedia,
                        EVasCategory.speedBonuses,
                        EVasCategory.tv,
                    ]),
                    withContentData: 1,
                    withTVPacketsData: 1,
                    trId,
                }),
            ),
            doRequest.gamersBase<IGetLinkResponseDto>(
                profileLink(providerId, domain),
            ),
        ]);

        if (vasResponse.isSuccess) {
            const connected: IVasInfo[] = [];
            const antiviruses: IVasInfo[] = [];

            let isGamersBaseActive = false;

            for (const item of vasResponse.payload) {
                if (item.activeVas) {
                    connected.push(item);
                }

                if (!item.activeVas && item.contentTypeId === EVasContentType.antiviruses && withAntiviruses) {
                    antiviruses.push(item);
                }

                if (item.activeVas && GAMERS_BASE_SERVICE_IDS.includes(item.serviceId)) {
                    isGamersBaseActive = true;
                }
            }

            batch(() => {
                dispatch(vasLoaded(connected));
                dispatch(vasAntivirusLoaded(antiviruses));

                if (gamesBaseLinkResponse.isSuccess && isGamersBaseActive) {
                    dispatch(setGamersBaseUrl(gamesBaseLinkResponse.payload.url));
                }
            });
        } else {
            dispatch(vasLoadError());
        }
    };
}

export function loadSubscriptions() {
    return async(dispatch: TDispatch, getState: TGetState): Promise<void> => {
        const state = getState();
        const subscriptions = selectSubscriptions(state);

        if (subscriptions.length) {
            return;
        }

        dispatch(vasSubscriptionsLoading());

        const response = await doRequest.apiProfile<IVasInfo[]>(
            vas({
                categories: `${EVasCategory.onlineCinemas};`,
                mobileVas: 0,
                withContentData: 1,
                withTVPacketsData: 0,
            }),
        );

        if (response.isSuccess) {
            const subscriptions = response.payload
                .filter(vas => !!vas.image)
                .sort((a, b) => a.activeVas - b.activeVas);

            dispatch(vasSubscriptionsLoaded(subscriptions));
        } else {
            captureException(
                response.error,
                {
                    extra: {
                        message: response.error.message,
                        url: 'api-profile/v1/vas',
                        status: response.error.status,
                        code: response.error.code,
                    },
                    tags: {
                        place: 'api-profile',
                        type: 'load-subscriptions',
                    },
                },
            );
            dispatch(vasSubscriptionsError());
        }
    };
}

export function loadVasIfChange() {
    return async(dispatch: TAppThunkDispatch, getState: TGetState): Promise<void> => {
        const state = getState();

        if (state.vas.vasDisablingStatus !== EVasDisablingStatus.default) {
            dispatch(vasDisablingStatusChange(EVasDisablingStatus.default));
            dispatch(loadVas({ isSkipLoadCheck: true }));
        }
    };
}
